import { useEffect, useState } from 'react';

/**
 * Picks a rendering tier once, up front, from cheap device signals.
 *
 *  null   — not decided yet. Nothing 3D mounts, so a reduced-motion visitor
 *           never even downloads the Three.js chunk.
 *  'off'  — the visitor asked for reduced motion, or WebGL is unavailable.
 *           Everything 3D is skipped and the CSS backdrop carries the page.
 *  'low'  — phones and low-core machines: fewer petals, capped DPR, no AA.
 *  'high' — everything on.
 */
export function usePerfTier() {
  const [tier, setTier] = useState(null);

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !hasWebGL()) {
      setTier('off');
      return;
    }

    const cores = navigator.hardwareConcurrency || 4;
    const memory = navigator.deviceMemory || 4;
    const coarse = window.matchMedia?.('(pointer: coarse)').matches;
    const narrow = window.innerWidth < 820;

    const weak = cores <= 4 || memory <= 4 || (coarse && narrow);
    setTier(weak ? 'low' : 'high');
  }, []);

  return tier;
}

function hasWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(
      window.WebGLRenderingContext &&
        (canvas.getContext('webgl2') || canvas.getContext('webgl'))
    );
  } catch (e) {
    return false;
  }
}

/** Per-tier knobs, kept in one place so the scenes stay declarative. */
export const TIER_SETTINGS = {
  off: { petals: 0, sparkles: 0, dpr: [1, 1], antialias: false, shadows: false },
  low: { petals: 34, sparkles: 40, dpr: [1, 1.5], antialias: false, shadows: false },
  high: { petals: 88, sparkles: 110, dpr: [1, 2], antialias: true, shadows: false },
};
